/**
 * Push registration, as a lifecycle rather than a call.
 *
 * The server sends an employee the notification about their OWN pass — «Приход» /
 * «Уход», name and time — to the account the personnel card is linked to. Nothing on
 * the client triggers that; the only thing the cabinet owes the platform is a device
 * token, kept current, and withdrawn when this device stops being this person's.
 *
 * Which is three obligations, and each is a way to get it wrong:
 *
 * 1. **Register after sign-in, not on launch.** A token registered before anyone is
 *    signed in has no account to attach to.
 * 2. **Re-register when the provider rotates the token.** An FCM token changes on
 *    reinstall and on restore-to-a-new-phone. A registration never renewed is a device
 *    that silently stops receiving, and nothing anywhere reports it.
 * 3. **Revoke on sign-out, BEFORE the session is gone.** `DELETE /auth/push-token/{id}`
 *    needs the session that owns it; after `forgetTokens()` there is nothing left to
 *    authenticate with. Miss this and the next person to sign in on this phone gets
 *    the previous employee's arrival notifications.
 *
 * The stored registration lives in AsyncStorage rather than the Keychain, unlike the
 * session: a device push token is not a credential — it is an address, it is handed to
 * the OS and to a third-party push service in the clear, and holding it does not let
 * anyone act as the user. What the Keychain protects is the refresh token.
 *
 * ⚠️ Whether anything can be delivered at all depends on `push/transport.ts`, which is
 * where the provider is (not yet) wired up and where the reasons are written down.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';

import {registerPushToken, revokePushToken} from '@/entities/account/api';
import {
  PUSH_PLATFORM,
  PUSH_PROVIDER_NAME,
  pushMessaging,
  type PushMessage,
} from '@/shared/push/transport';

export {PUSH_PROVIDER_NAME};
export type {PushMessage};

const STORAGE_KEY = 'samvision.push.registration';

export type PushRegistration = {
  token: string;
  /**
   * Null when the server did not name the row it created. The registration still
   * works — it simply cannot be revoked from here, which the UI says out loud rather
   * than pretending the switch is symmetric.
   */
  tokenId: string | null;
};

/** Why pushes are or are not working, in one word the UI can branch on. */
export type PushStatus =
  /** No provider in this build — see transport.ts. */
  | 'unconfigured'
  /** Provider present, user has not been asked or said no. */
  | 'denied'
  /** Provider present, token registered with the gateway. */
  | 'registered'
  /** Provider present, nothing registered yet. */
  | 'idle';

async function readStored(): Promise<PushRegistration | null> {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<PushRegistration>;
    return parsed.token ? {token: parsed.token, tokenId: parsed.tokenId ?? null} : null;
  } catch {
    return null;
  }
}

async function writeStored(value: PushRegistration | null): Promise<void> {
  try {
    if (value) {
      await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } else {
      await AsyncStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage failing means we re-register next launch — wasteful, not broken.
  }
}

/** True when a provider is wired into this build at all. */
export function pushAvailable(): boolean {
  return pushMessaging() !== null;
}

export async function pushStatus(): Promise<PushStatus> {
  if (!pushAvailable()) {
    return 'unconfigured';
  }
  return (await readStored()) ? 'registered' : 'idle';
}

/**
 * Register this device, or refresh the registration if the token changed.
 *
 * Idempotent by design: called on every sign-in and every cold start with a live
 * session, it does nothing when the provider's token still matches what was last
 * registered. Re-posting an unchanged token would work, but it creates a second row on
 * the server for the same device — and with no route to list them, the duplicates are
 * unreachable forever after.
 */
export async function syncPushRegistration(
  deviceLabel: string,
): Promise<PushStatus> {
  const messaging = pushMessaging();
  if (!messaging) {
    return 'unconfigured';
  }

  try {
    const granted = await messaging.requestPermission();
    if (!granted) {
      return 'denied';
    }
    const token = await messaging.getToken();
    if (!token) {
      return 'denied';
    }

    const stored = await readStored();
    if (stored?.token === token) {
      return 'registered';
    }

    // The old row is withdrawn FIRST: the device now answers to a new address, and
    // leaving the previous one registered means the server keeps trying to deliver to
    // an address this phone no longer has.
    if (stored?.tokenId) {
      await revokePushToken(stored.tokenId).catch(() => {});
    }

    const {id} = await registerPushToken({
      token,
      platform: PUSH_PLATFORM,
      deviceLabel,
    });
    await writeStored({token, tokenId: id});
    return 'registered';
  } catch {
    // Push is an enhancement to a session, never a precondition for one: a failure
    // here must not stop a sign-in that has otherwise succeeded.
    return 'idle';
  }
}

/**
 * Withdraw this device's registration. Call while the session is still alive.
 *
 * Errors are swallowed on purpose — a user who cannot reach the network must still be
 * able to sign out. The local record is dropped either way, so the next sign-in
 * registers afresh rather than trusting a row that may or may not still exist.
 */
export async function clearPushRegistration(): Promise<void> {
  const stored = await readStored();
  if (stored?.tokenId) {
    await revokePushToken(stored.tokenId).catch(() => {});
  }
  await writeStored(null);
}

/**
 * Watch for provider-side rotation. Returns an unsubscribe, or a no-op when there is
 * no provider. See obligation (2) above for why this is not optional.
 */
export function watchPushToken(deviceLabel: string): () => void {
  const messaging = pushMessaging();
  if (!messaging) {
    return () => {};
  }
  return messaging.onTokenRefresh(() => {
    void syncPushRegistration(deviceLabel);
  });
}

export type PushMessageHandlers = {
  /** Arrived while the app is on screen — the OS shows nothing, so the caller must. */
  onForeground: (message: PushMessage) => void;
  /** The user tapped the notification, from the background or from a cold start. */
  onOpen: (message: PushMessage) => void;
};

/**
 * The tap that cold-started the app is handed over once per process. The provider keeps
 * returning it for the life of the process, and the watcher re-subscribes whenever the
 * signed-in shell remounts (persona switch, sign-out and back in) — without this the
 * same old notification would keep pulling the user to its screen.
 */
let initialOpenConsumed = false;

/**
 * Listen for incoming pushes. Mount where there is a session and a navigator to act on
 * them; returns an unsubscribe, or a no-op when the build has no provider.
 */
export function watchPushMessages(handlers: PushMessageHandlers): () => void {
  const messaging = pushMessaging();
  if (!messaging) {
    return () => {};
  }

  let active = true;
  if (!initialOpenConsumed) {
    initialOpenConsumed = true;
    messaging
      .getInitialOpened()
      .then(message => {
        if (active && message) {
          handlers.onOpen(message);
        }
      })
      .catch(() => {});
  }

  const stopForeground = messaging.onForegroundMessage(handlers.onForeground);
  const stopOpened = messaging.onOpened(handlers.onOpen);
  return () => {
    active = false;
    stopForeground();
    stopOpened();
  };
}
